import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import { LoadingIcon } from "components/Common/LoadingIcon"
import { Title } from "components/Common/Title"
import { useOrganizations } from "../useOrganizations"
import analyticsService from "services/analyticsService"
import { hiabColors } from "theme"
import type { IAnalyticsAdoption } from "types/analyticsTypes"

type Scope = "global" | "org"

export function FeatureAdoptionTab() {
  const { organizations, error: orgError, refetch: refetchOrgs } = useOrganizations()

  const [scope, setScope] = useState<Scope>("global")
  const [selectedOrgId, setSelectedOrgId] = useState<string>("")
  const [data, setData] = useState<IAnalyticsAdoption | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (orgId?: string) => {
    setLoading(true)
    setError(null)
    try {
      const result = await analyticsService.getAdoption(orgId)
      setData(result)
    } catch {
      setError("Failed to load feature adoption data.")
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch on mount (global scope)
  useEffect(() => {
    load()
  }, [load])

  const handleScopeChange = (event: SelectChangeEvent<Scope>) => {
    const newScope = event.target.value as Scope
    setScope(newScope)
    setSelectedOrgId("")
    if (newScope === "global") {
      load()
    }
    // If org scope, wait for org selection before fetching
  }

  const handleOrgChange = (event: SelectChangeEvent<string>) => {
    const orgId = event.target.value
    setSelectedOrgId(orgId)
    if (orgId) {
      load(orgId)
    }
  }

  const retryLoad = () => {
    if (scope === "global") {
      load()
    } else if (selectedOrgId) {
      load(selectedOrgId)
    }
  }

  const lessons = [...(data?.lessons ?? [])].sort((a, b) => a.completionRate - b.completionRate)
  const allEquipment = [...(data?.equipment ?? [])].sort((a, b) => b.sessionCount - a.sessionCount)
  const cranes = allEquipment.filter((e) => e.parameterName === "crane")
  const tools = allEquipment.filter((e) => e.parameterName === "tool")

  return (
    <Box>
      {/* Scope controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Scope</InputLabel>
          <Select<Scope> value={scope} label="Scope" onChange={handleScopeChange}>
            <MenuItem value="global">Global</MenuItem>
            <MenuItem value="org">Per Organization</MenuItem>
          </Select>
        </FormControl>

        {scope === "org" && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Organization</InputLabel>
            <Select<string> value={selectedOrgId} label="Organization" onChange={handleOrgChange}>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Org fetch error */}
      {orgError && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={refetchOrgs}>Retry</Button>}>
          {orgError}
        </Alert>
      )}

      {/* Guard: org scope but no org selected */}
      {scope === "org" && !selectedOrgId && (
        <Typography variant="body2" color="text.secondary">
          Select an organization to view data
        </Typography>
      )}

      {/* Loading */}
      {loading && (scope === "global" || !!selectedOrgId) && <LoadingIcon />}

      {/* Error */}
      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retryLoad}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Charts */}
      {!loading && !error && data && (
        <Grid container spacing={3} direction="column">
          {/* Lesson Completion Rates */}
          <Grid item>
            <Paper sx={{ p: 3 }}>
              <Title>LESSON COMPLETION RATES</Title>
              {lessons.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No lesson data available for this organisation.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Lesson</TableCell>
                        {scope === "global" && (
                          <TableCell sx={{ fontWeight: "bold" }} align="right">Orgs Using</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Total Sessions</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Completed</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Quit</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Completion Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lessons.map((lesson) => (
                        <TableRow key={lesson.lessonId} hover>
                          <TableCell>{lesson.lessonName ?? `Unknown (${lesson.lessonId})`}</TableCell>
                          {scope === "global" && (
                            <TableCell align="right">{lesson.orgsCount ?? "—"}</TableCell>
                          )}
                          <TableCell align="right">{lesson.totalSessions}</TableCell>
                          <TableCell align="right">{lesson.completed}</TableCell>
                          <TableCell align="right">{lesson.quit}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: lesson.completionRate < 50 ? hiabColors.redPrimary : hiabColors.bluePrimary,
                              fontWeight: "bold",
                            }}
                          >
                            {lesson.completionRate}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Crane Usage */}
          <Grid item>
            <Paper sx={{ p: 3 }}>
              <Title>CRANE USAGE</Title>
              {cranes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No crane data available for this organisation.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Crane</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Sessions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cranes.map((item) => (
                        <TableRow key={item.parameterValue} hover>
                          <TableCell>{item.parameterValue || "Unknown"}</TableCell>
                          <TableCell align="right">{item.sessionCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Tool Usage */}
          <Grid item>
            <Paper sx={{ p: 3 }}>
              <Title>TOOL USAGE</Title>
              {tools.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No tool data available for this organisation.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Tool</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Sessions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tools.map((item) => (
                        <TableRow key={item.parameterValue} hover>
                          <TableCell>{item.parameterValue || "Unknown"}</TableCell>
                          <TableCell align="right">{item.sessionCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
